import { createSlice } from "@reduxjs/toolkit";

/**
 *
 * compile-time feature flags
 *
 * Storing these in redux to enable per-bubble features.
 */
export interface Features {
  readonly chatLogs: boolean;
  readonly calendarExport: boolean;
  readonly districtMode: boolean;
  readonly showVolunteerStats: boolean;
  readonly loginAsTestUser: boolean;
  readonly payments: boolean;
  readonly reduxDevtools: boolean;
  readonly supportChat: boolean;
  readonly homePageVideos: boolean;
  readonly homePageStats: boolean;
  readonly notifications: boolean;
  readonly emailPreferences: boolean;
  readonly organizations: boolean;
  /** show background check features */
  readonly backgroundChecks: boolean;
  /** can user buy their own checks? */
  readonly backgroundChecksPaidByUser: boolean;
  /** can schools buy a check for a user? */
  readonly backgroundChecksPaidBySchool: boolean;
  /** show resumes */
  readonly showResumes: boolean;
  /** show candidate search */
  readonly showCandidateSearch: boolean;
  /** show user history */
  readonly showUserHistory: boolean;
  /** show AI Tutor */
  readonly showAITutor: boolean;
  /** Environment-Specific Intro Video */
  readonly isSpaceFlorida: boolean;
  /** show Donations */
  readonly showDonations: boolean;
  readonly showRoomAndEventSearch: boolean;
  /** show sales portal features */
  readonly showSalesPortal: boolean;
  /** show Google SSO login option */
  readonly showGoogleSSO: boolean;
  /** show public organization/school/district pages and slug editing */
  readonly publicPages: boolean;
  /** Emergency Management admin tab, school visitor management routes, and related home/nav entry points */
  readonly emergencyManagement: boolean;
  /** Platform Insights → User Metrics tab */
  readonly platformInsightsUserMetrics: boolean;
  /** Platform Insights → Organization Metrics tab */
  readonly platformInsightsOrganizationMetrics: boolean;
  /** Platform Insights → Student Candidacy tab (also requires showResumes) */
  readonly platformInsightsStudentCandidacy: boolean;
  /** Platform Insights → Candidate Search tab (also requires showResumes) */
  readonly platformInsightsCandidateSearch: boolean;
  /** Platform Insights → Skills & Certifications tab (also requires showResumes) */
  readonly platformInsightsSkillsCertifications: boolean;
  /** Platform Insights → Talent Alignment tab (also requires showResumes) */
  readonly platformInsightsTalentAlignment: boolean;
}

/** string enum of all feature flags */
export type Feature = keyof Features;

/** convert true-ish strings to booleans */
const toBoolean = (value: string | undefined) => value !== undefined && /^(true|1|yes)$/i.test(value);

export const initialState: Features = {
  backgroundChecks: toBoolean(import.meta.env.REACT_APP_FEATURES_BACKGROUND_CHECKS),
  backgroundChecksPaidBySchool: toBoolean(import.meta.env.REACT_APP_FEATURES_BACKGROUND_CHECKS_PAID_BY_SCHOOL),
  backgroundChecksPaidByUser: toBoolean(import.meta.env.REACT_APP_FEATURES_BACKGROUND_CHECKS_PAID_BY_USER),
  calendarExport: toBoolean(import.meta.env.REACT_APP_FEATURES_CALENDAR_EXPORT),
  chatLogs: toBoolean(import.meta.env.REACT_APP_FEATURES_CHAT_LOGS),
  districtMode: toBoolean(import.meta.env.REACT_APP_FEATURES_DISTRICT_MODE),
  emailPreferences: toBoolean(import.meta.env.REACT_APP_FEATURES_EMAIL_PREFERENCES),
  emergencyManagement: toBoolean(import.meta.env.REACT_APP_FEATURES_EMERGENCY_MANAGEMENT),
  homePageStats: toBoolean(import.meta.env.REACT_APP_FEATURES_HOME_PAGE_STATS),
  homePageVideos: toBoolean(import.meta.env.REACT_APP_FEATURES_HOME_PAGE_VIDEOS),
  isSpaceFlorida: toBoolean(import.meta.env.REACT_APP_IS_SPACE_FLORIDA),
  loginAsTestUser: toBoolean(import.meta.env.REACT_APP_FEATURES_LOGIN_AS_TEST_USER),
  notifications: toBoolean(import.meta.env.REACT_APP_FEATURES_NOTIFICATIONS),
  organizations: toBoolean(import.meta.env.REACT_APP_FEATURES_ORGANIZATIONS),
  payments: toBoolean(import.meta.env.REACT_APP_FEATURES_PAYMENT),
  platformInsightsCandidateSearch: toBoolean(import.meta.env.REACT_APP_FEATURES_PLATFORM_INSIGHTS_CANDIDATE_SEARCH),
  platformInsightsOrganizationMetrics: toBoolean(
    import.meta.env.REACT_APP_FEATURES_PLATFORM_INSIGHTS_ORGANIZATION_METRICS,
  ),
  platformInsightsSkillsCertifications: toBoolean(
    import.meta.env.REACT_APP_FEATURES_PLATFORM_INSIGHTS_SKILLS_CERTIFICATIONS,
  ),
  platformInsightsStudentCandidacy: toBoolean(import.meta.env.REACT_APP_FEATURES_PLATFORM_INSIGHTS_STUDENT_CANDIDACY),
  platformInsightsTalentAlignment: toBoolean(import.meta.env.REACT_APP_FEATURES_PLATFORM_INSIGHTS_TALENT_ALIGNMENT),
  platformInsightsUserMetrics: toBoolean(import.meta.env.REACT_APP_FEATURES_PLATFORM_INSIGHTS_USER_METRICS),
  publicPages: toBoolean(import.meta.env.REACT_APP_FEATURES_PUBLIC_PAGES),
  reduxDevtools: toBoolean(import.meta.env.REACT_APP_FEATURES_REDUX_DEVTOOLS),
  showAITutor: toBoolean(import.meta.env.REACT_APP_FEATURES_AI_TUTOR),
  showCandidateSearch: toBoolean(import.meta.env.REACT_APP_FEATURES_CANDIDATE_SEARCH),
  showDonations: toBoolean(import.meta.env.REACT_APP_FEATURES_DONATIONS),
  showGoogleSSO: toBoolean(import.meta.env.REACT_APP_FEATURES_GOOGLE_SSO),
  showResumes: toBoolean(import.meta.env.REACT_APP_FEATURES_RESUMES),
  showRoomAndEventSearch: toBoolean(import.meta.env.REACT_APP_SHOW_ROOM_EVENT_SEARCH),
  showSalesPortal: toBoolean(import.meta.env.REACT_APP_FEATURES_SALES_PORTAL),
  showUserHistory: toBoolean(import.meta.env.REACT_APP_FEATURES_SHOW_USER_HISTORY),
  showVolunteerStats: toBoolean(import.meta.env.REACT_APP_FEATURES_SHOW_VOLUNTEER_STATS),
  supportChat: toBoolean(import.meta.env.REACT_APP_FEATURES_SUPPORT_CHAT),
};

const slice = createSlice({
  initialState,
  name: "features",
  reducers: {},
});

export default slice.reducer;

/** per-domain overrides from the .env files  */
export const overrides: Record<string, Partial<Features>> = {
  "alachua.fl.crowdcoursing.com": {
    districtMode: true,
    emergencyManagement: false,
    isSpaceFlorida: false,
    organizations: true,
    payments: false,
    platformInsightsCandidateSearch: false,
    platformInsightsOrganizationMetrics: true,
    platformInsightsSkillsCertifications: false,
    platformInsightsStudentCandidacy: false,
    platformInsightsTalentAlignment: false,
    platformInsightsUserMetrics: true,
    showAITutor: false,
    showCandidateSearch: false,
    showDonations: true,
    showGoogleSSO: true,
    showResumes: false,
    showRoomAndEventSearch: true,
    showSalesPortal: false,
    showVolunteerStats: true,
  },
  "app.crowdcoursing.com": {
    backgroundChecks: false,
    backgroundChecksPaidByUser: false,
    districtMode: true,
    emergencyManagement: false,
    isSpaceFlorida: false,
    organizations: true,
    payments: false,
    platformInsightsCandidateSearch: false,
    platformInsightsOrganizationMetrics: true,
    platformInsightsSkillsCertifications: false,
    platformInsightsStudentCandidacy: false,
    platformInsightsTalentAlignment: false,
    platformInsightsUserMetrics: true,
    showAITutor: false,
    showCandidateSearch: false,
    showDonations: true,
    showGoogleSSO: true,
    showResumes: false,
    showRoomAndEventSearch: true,
    showSalesPortal: true,
    showVolunteerStats: true,
    supportChat: true,
  },
  "app.spacefloridaorbit.com": {
    backgroundChecks: false,
    backgroundChecksPaidByUser: false,
    districtMode: true,
    emergencyManagement: false,
    isSpaceFlorida: true,
    organizations: true,
    payments: false,
    platformInsightsCandidateSearch: true,
    platformInsightsOrganizationMetrics: true,
    platformInsightsSkillsCertifications: true,
    platformInsightsStudentCandidacy: true,
    platformInsightsTalentAlignment: true,
    platformInsightsUserMetrics: true,
    showAITutor: false,
    showCandidateSearch: true,
    showDonations: false,
    showGoogleSSO: true,
    showResumes: true,
    showRoomAndEventSearch: true,
    showSalesPortal: false,
    showVolunteerStats: false,
    supportChat: true,
  },
  "district-sandbox.crowdcoursing.com": {
    backgroundChecks: true,
    backgroundChecksPaidBySchool: true,
    backgroundChecksPaidByUser: true,
    districtMode: true,
    emergencyManagement: true,
    isSpaceFlorida: false,
    organizations: true,
    payments: false,
    platformInsightsCandidateSearch: true,
    platformInsightsOrganizationMetrics: true,
    platformInsightsSkillsCertifications: true,
    platformInsightsStudentCandidacy: true,
    platformInsightsTalentAlignment: true,
    platformInsightsUserMetrics: true,
    publicPages: true,
    showAITutor: false,
    showCandidateSearch: false,
    showDonations: true,
    showGoogleSSO: true,
    showResumes: false,
    showRoomAndEventSearch: true,
    showSalesPortal: false,
    showUserHistory: true,
    showVolunteerStats: true,
  },
  "sandbox.crowdcoursing.com": {
    backgroundChecks: true,
    backgroundChecksPaidByUser: true,
    districtMode: true,
    emergencyManagement: false,
    isSpaceFlorida: true,
    organizations: true,
    payments: false,
    platformInsightsCandidateSearch: true,
    platformInsightsOrganizationMetrics: true,
    platformInsightsSkillsCertifications: true,
    platformInsightsStudentCandidacy: true,
    platformInsightsTalentAlignment: true,
    platformInsightsUserMetrics: true,
    publicPages: true,
    showAITutor: false,
    showCandidateSearch: true,
    showDonations: false,
    showGoogleSSO: true,
    showResumes: true,
    showRoomAndEventSearch: true,
    showSalesPortal: false,
    showUserHistory: true,
    showVolunteerStats: false,
  },
};
